import { Component, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { AuthenticationService } from '@shared/services/authentication.service';
import { KalturaService } from '@shared/services/kaltura.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { switchMap, map, catchError } from 'rxjs/operators';
import { Observable, zip } from 'rxjs';
import { Hub, HubMode } from '@shared/models';

@Component({
  selector: 'kaltura-account-form',
  templateUrl: './kaltura-account-form.component.html',
  styleUrls: ['./kaltura-account-form.component.scss']
})
export class KalturaAccountFormComponent implements OnInit {
  @Input() hub: Hub
  @Output() connectionChange = new EventEmitter<void>();

  kalturaAccountForm: FormGroup;
  clientFeedback: 'ok' | 'ko';
  serverFeedback: 'ok' | 'ko' | 'insufficient rights';
  kalturaCloudServersOptions: any = [];
  constructor(
    private _kalturaService: KalturaService,
    private _courseApiService: CoursesAPIService,
    private _authService: AuthenticationService,
  ) {

    const kalturaCloudServers = {
      'US': 'https://www.kaltura.com',
      'AP': 'https://api.syp2.ovp.kaltura.com', //'https://api.ap.kaltura.com',
      'CA': 'https://api.cap2.ovp.kaltura.com', //'https://api.ca.kaltura.com',
      'DE': 'https://api.frp2.ovp.kaltura.com', //'https://api.de.kaltura.com',
      'EU': 'https://api.irp2.ovp.kaltura.com', //'https://api.eu.kaltura.com',
      'SG': 'https://api.sgp2.ovp.kaltura.com', //'https://api.sg.kaltura.com',
    }

    this.kalturaCloudServersOptions = Object.entries(kalturaCloudServers).map(
      ([key, value]) => ({
        label: key,
        value,
      })
    );
    this.kalturaAccountForm = new FormGroup({
      clientAppToken: new FormControl('', { validators: Validators.required }),
      serverAppToken: new FormControl('', { validators: Validators.required }),
      kalturaIdMethod: new FormControl('email'),
      serviceUrl: new FormControl(kalturaCloudServers['US'])
    });
  }

  async ngOnInit(): Promise<void> {
    const creds = await this._courseApiService.getHubIntegrations(this.hub.id).toPromise();
    // Default value for kalturaIdMethod if unset
    if (creds.kaltura) {
      const kalturaIdMethod = (creds.kaltura.idMethod) ? creds.kaltura.idMethod : 'email';
      this.kalturaAccountForm.reset({
        clientAppToken: JSON.stringify(creds.kaltura.client),
        serverAppToken: JSON.stringify(creds.kaltura.server),
        kalturaIdMethod: kalturaIdMethod,
        serviceUrl: creds.kaltura.serviceUrl
      });
    }
    this.checkClientAppToken().subscribe();
    this.checkServerAppToken().subscribe();
  }

  openHelp(): void {
    if (this.hub.mode === HubMode.space) {
      window.open('https://help.spaces.wondavr.com/en/articles/2630822-kaltura-integration');
    } else {
      window.open('https://intercom.help/wonda-pro/en/articles/11331066-sso-integration');
    }
  }

  isConnected(): boolean {
    return this.serverFeedback === 'ok' && this.clientFeedback === 'ok';
  }

  isNotConnected(): boolean {
    return this.serverFeedback === 'ko' || this.clientFeedback === 'ko';
  }

  checkToken(value: string) {
    try {
      return JSON.parse(this.kalturaAccountForm.value[value]);
    } catch (error) {
      console.warn('JSON parsing error', error);
      this.kalturaAccountForm.controls[value].setValue('{}');
    }
  }

  updateKalturaAccount(): void {
    let credentials: any = {};
    const clientAppToken = this.checkToken('clientAppToken');
    const serverAppToken = this.checkToken('serverAppToken');
    credentials = {
      server: serverAppToken,
      client: clientAppToken,
      idMethod: this.kalturaAccountForm.value.kalturaIdMethod,
      serviceUrl: this.kalturaAccountForm.value.serviceUrl
    };
    this._courseApiService
      .patchHubIntegrations(this.hub.id, { op: 'set', values: { kaltura: credentials } })
      .pipe(
        switchMap(() => {
          this.connectionChange.emit();
          return zip(this.checkClientAppToken(), this.checkServerAppToken());
        })
      )
      .subscribe();
  }

  checkClientAppToken(): Observable<string> {
    return this._kalturaService.startKalturaSession(this.hub.id, this._authService.wondaUser).pipe(
      map(() => {
        this.clientFeedback = 'ok';
        return 'ok';
      }),
      catchError(err => {
        console.error(err);
        this.clientFeedback = 'ko';
        return 'ko';
      })
    )
  }

  checkServerAppToken(): Observable<string> {
    return this._kalturaService.checkBackendConfig(this.hub.id).pipe(
      map((resp) => {
        this.serverFeedback = resp.backSession;
        return resp.backSession;
      }),
      catchError(err => {
        console.error(err);
        this.serverFeedback = 'ko';
        return 'ko';
      })
    )
  }
}
